/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { ActionButtonGroup, Button, Typography, Typography2 } from '@coursera/cds-core';

import withCalendarData from 'bundles/calendar-sync/withCalendarData';

import _t from 'i18n!nls/account-settings';

import ToggleShouldDisplayCalendarMutation from './queries/ToggleShouldDisplayCalendarMutation.graphql';
import type {
  ToggleCalendarEnabledv2Mutation,
  ToggleCalendarEnabledv2MutationVariables,
} from './queries/__generated__/ToggleShouldDisplayCalendarMutation';

const styles = {
  calDesc: css`
    margin: 0 0 20px;
  `,
  topSectionTitle: css`
    margin: 0;
    padding-bottom: 32px;
  `,
  perferenceHeading: css`
    border-top: none;
    padding-bottom: 32px;
  `,
  buttonList: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  buttonSpacer: css`
    display: inline-block;
    margin-right: 0.75rem;
  `,
};

type InnerProps = {
  toggleCalendarEnabled: (x0: {}) => Promise<any>;
  calendar?: {
    google: string;
    webcal: string;
    ics: string;
  };
};

const withCalendarToggleMutation = graphql<
  {},
  ToggleCalendarEnabledv2Mutation,
  ToggleCalendarEnabledv2MutationVariables,
  {}
>(ToggleShouldDisplayCalendarMutation, {
  name: 'toggleCalendarEnabled',
});

export const CalendarSettings = ({ calendar, toggleCalendarEnabled }: InnerProps) => {
  const retrackedTrack = useRetracked();
  const [inProgressButtonID, setInProgressButtonID] = useState(-1);
  const { google, webcal } = calendar || {};

  const handleCalendarButtonClick = (id: number, link: string | undefined, trackingName: string) => () => {
    if (!link) {
      return;
    }

    setInProgressButtonID(id);

    retrackedTrack({
      trackingName,
      action: 'click',
      trackingData: {},
    });

    toggleCalendarEnabled({
      variables: {
        body: {
          userId: user.get().id.toString(),
          shouldDisplayContent: true,
        },
      },
      context: { clientName: 'gatewayGql' },
    }).then(() => {
      window.open(link, '_blank');

      setInProgressButtonID(-1);
    });
  };

  const makeDownloadButton = (label: string, link: string | undefined, trackingName: string) => (id: number) => ({
    label,
    trackingName,
    key: trackingName,
    onClick: handleCalendarButtonClick(id, link, trackingName),
  });

  const downloadButtons = [
    makeDownloadButton(_t('Google Calendar'), google, 'download_cal_btn_google'),
    makeDownloadButton(_t('Apple Calendar'), webcal, 'download_cal_btn_apple'),
    makeDownloadButton(_t('Other Calendar'), webcal, 'download_cal_btn_other'),
  ];

  return (
    <div className="rc-CalendarSetttings">
      <Typography id="a11y-navigation-heading" variant="h1" css={[styles.perferenceHeading, styles.topSectionTitle]}>
        {_t('Calendar Sync')}
      </Typography>
      <div>
        <div css={styles.calDesc}>
          {_t(`Automatically sync all the deadlines and other related items from all active courses to your calendar.`)}
        </div>
        <Typography2 variant="subtitleMedium" component="h2">
          {_t('Add to my calendar')}
        </Typography2>
        <ActionButtonGroup variant="secondary" size="small">
          <ul role="list" css={styles.buttonList}>
            {downloadButtons.map((props, idx) => {
              const { onClick, label } = props(idx);
              const isInProgress = inProgressButtonID === idx;
              return (
                <li role="listitem" key={label} css={styles.buttonSpacer}>
                  <ActionButtonGroup.Button
                    component={Button}
                    type="button"
                    loading={isInProgress}
                    onClick={onClick}
                    aria-label={label}
                  >
                    {label}
                  </ActionButtonGroup.Button>
                </li>
              );
            })}
          </ul>
        </ActionButtonGroup>
      </div>
    </div>
  );
};

export default compose<InnerProps, {}>(withCalendarToggleMutation, withCalendarData)(CalendarSettings);
